import * as React from 'react'
import { cn } from '../../lib/utils'

export interface PanelCardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
	/** Page / card title text (e.g. "2-к квартира") */
	title: React.ReactNode
	/** Optional status badge rendered inline next to the title */
	badge?: React.ReactNode
	/** Description / subtitle line below title */
	description?: React.ReactNode
	/** Optional avatar / icon rendered on the left */
	avatar?: React.ReactNode
	/** Optional action slot rendered on the right of the title row (e.g. "Скачать PDF") */
	action?: React.ReactNode
	/** Optional close button rendered at the far right */
	onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void
	/** Optional stages row rendered below the title block */
	stages?: React.ReactNode
}

const CloseIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<path d="M18 6 6 18M6 6l12 12"/>
	</svg>
)

/**
 * Composite header for card-like panels.
 * Maps to Figma "Card Header" with optional avatar / badge / action / close / stages row.
 */
const PanelCardHeader = React.forwardRef<HTMLDivElement, PanelCardHeaderProps>(
	(
		{
			className,
			title,
			badge,
			description,
			avatar,
			action,
			onClose,
			stages,
			...props
		},
		ref
	) => (
		<div
			ref={ref}
			className={cn(
				'flex flex-col gap-3 border-b border-(--color-border) bg-(--color-background) p-4',
				className
			)}
			{...props}
		>
			<div className="flex items-center gap-4 w-full">
				{avatar && <div className="shrink-0">{avatar}</div>}
				<div className="flex flex-1 flex-col gap-1 min-w-0">
					<div className="flex items-center gap-3 w-full">
						<div className="flex flex-1 items-center gap-3 min-w-0">
							<span className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground) whitespace-nowrap">
								{title}
							</span>
							{badge}
						</div>
						{action}
						{onClose && (
							<button
								type="button"
								onClick={onClose}
								aria-label="Close"
								className="p-0.5 rounded-sm text-(--color-foreground) hover:bg-(--color-muted) transition-colors duration-150 cursor-pointer"
							>
								<CloseIcon />
							</button>
						)}
					</div>
					{description && (
						<p className="text-base leading-6 text-(--color-muted-foreground) w-full">
							{description}
						</p>
					)}
				</div>
			</div>
			{stages}
		</div>
	)
)

PanelCardHeader.displayName = 'PanelCardHeader'
export { PanelCardHeader }
